import { useEffect, useRef } from 'react';
import { CdnImage } from '../CdnImage';
import { VideoStill } from '../VideoStill';
import { CDN } from '../../lib/cdn';
import { SignUpButton, TalkToFoundersButton } from '../CTAButtons';
import { TimelinePlayhead } from '../TimelinePlayhead';
import { useTimelinePlayhead } from '../../hooks/useTimelinePlayhead';
import { COMPARE_ROWS, FILMMAKERS_FAQ, type CompareCell } from '../../content/forFilmmakersPage';
import { HeroLabel, PageFaqSection } from '../ds';
import { FilmmakerHighlights } from './FilmmakerHighlights';

const TIMELINE_CLIPS = [
  { stage: '00:00 · SCRIPT', slug: 'PAGE 1', k: 'Ingest', title: 'Upload the script', body: "That's the whole job. FDX, PDF, Fountain, or paste - every slug line, action, and annotation parsed in seconds.", image: CDN.filmmakers.ingest },
  { stage: '00:30 · BREAKDOWN', slug: 'SCENES', k: 'Breakdown', title: 'Every line becomes a shot', body: 'Characters, locations, and wardrobe enter the continuity graph - story logic checked before a frame renders.', image: CDN.filmmakers.breakdown },
  { stage: '01:00 · DIRECTION', slug: 'COVERAGE', k: 'Direction', title: 'Coverage, framed to the beat', body: 'A virtual DOP sets lens, framing, and blocking shot by shot. Override any one without touching the rest.', image: CDN.filmmakers.direction },
  { stage: '01:30 · CUT', slug: 'ASSEMBLY', k: 'Assembly', title: 'Scrub. Refine. Ship.', body: 'Watch the first cut, fix what you want, export at full fidelity or hand the project to your editor.', image: CDN.filmmakers.assembly },
];

const TIMELINE_RULER_LABELS = ['00:00', '00:30', '01:00', '01:30 · FIRST CUT'];

function DirectorTimeline() {
  const { sectionRef, active, inView, totalDuration, cycleKey } =
    useTimelinePlayhead(TIMELINE_CLIPS.length, { segmentMs: 5000 });
  const clipRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const mobile = window.matchMedia('(max-width: 560px)');
    if (!mobile.matches || !inView || active === 0) return;
    clipRefs.current[active]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [active, inView]);

  return (
    <section className="timeline band-dark" id="how" ref={sectionRef}>
      <div className="container">
        <div className="sec-head reveal">
          <span className="label-mono">- The workflow</span>
          <h2>
            Made for the way <em>directors work.</em>
          </h2>
          <p className="sub">
            Upload your screenplay. The pipeline reads it scene by scene, turns every beat into a composed shot, and
            assembles a continuous cut - with director-grade control over any shot.
          </p>
        </div>
        <div className="tl-rail reveal">
          <div className="tl-grid">
            <span className="tl-play-label play">PLAYHEAD</span>

            {TIMELINE_CLIPS.map((clip, i) => (
              <div key={`mark-${clip.stage}`} className={`tl-marker${active === i ? ' active' : ''}`}>
                <span className="tl-ruler-mark">{TIMELINE_RULER_LABELS[i]}</span>
              </div>
            ))}

            <div className="tl-scrub-wrap" data-active={active}>
              <span className="tl-scrub-time" aria-live="polite">
                {TIMELINE_RULER_LABELS[active]}
              </span>
              <div className="tl-playhead-row">
                <div className="tl-playhead-track-span">
                  <TimelinePlayhead totalDuration={totalDuration} inView={inView} cycleKey={cycleKey} />
                </div>
                {TIMELINE_CLIPS.map((clip, i) => (
                  <span
                    key={`dot-${clip.stage}`}
                    className={`tl-dot${active === i ? ' active' : ''}`}
                    style={{ gridColumn: i + 1 }}
                    aria-hidden="true"
                  />
                ))}
              </div>
            </div>

            {TIMELINE_CLIPS.map((clip, i) => (
              <div
                key={clip.stage}
                ref={(el) => { clipRefs.current[i] = el; }}
                className={`tl-clip${active === i ? ' now' : ''}`}
              >
                <span className="tl-stage">{clip.stage}</span>
                <div className="tl-frame">
                  <CdnImage
                    src={clip.image}
                    alt={clip.title}
                    loading="lazy"
                    className="tl-frame-img"
                  />
                </div>
                <div className="tl-meta">
                  <span className="k">{clip.k}</span>
                  <h3>{clip.title}</h3>
                  <p>{clip.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function VsCell({ cell }: { cell: CompareCell }) {
  if (cell.status === 'text') return <>{cell.label}</>;
  const cls = cell.status === 'yes' ? 'vs-yes' : cell.status === 'no' ? 'vs-no' : 'vs-part';
  return (
    <span className={cls}>
      <span className="vs-dot" />
      {cell.label}
    </span>
  );
}

export function ForFilmmakersSections() {
  return (
    <div className="solutions-page">
      <header className="fm-hero band-dark">
        <div className="container hero-grid">
            <div className="reveal">
              <HeroLabel>For filmmakers &amp; directors</HeroLabel>
              <h1 className="hero-headline">
                For Directors Who Think in Scenes, <em>Not Prompts.</em>
              </h1>
              <p className="hero-sub">
                Turn a script into a finished film — an AI movie maker built for directors, not prompt engineers.
                <br /><br />
                Upload a screenplay. Induce reads it scene by scene, casts your characters, and generates every shot
                with continuity and intent - pitch reel or short film, cut in an afternoon.
              </p>
              <div className="hero-ctas">
                <SignUpButton variant="primary" />
                <a href="#how" className="btn btn-ghost">
                  See how it works
                </a>
              </div>
              <div className="hero-note">Commercial rights on every tier · FDX · PDF · Fountain</div>
            </div>
            <div
              className="hero-still reveal d2"
              role="img"
              aria-label="AI filmmaking tools for directors - a screenplay becoming a directed cut in Induce"
            >
              <VideoStill
                src={CDN.videos.filmMaker}
                aria-label="AI filmmaking tools for directors - a screenplay becoming a directed cut in Induce"
              />
            </div>
        </div>
      </header>

      <section className="answer band-light" aria-labelledby="problem-h">
        <div className="container">
          <div className="tag">- The Problem</div>
          <div className="answer-body">
            <h2 id="problem-h" className="a-lead">
              <b>AI video gave filmmakers beautiful clips and no way to make them cohere into a film.</b>
            </h2>
            <p className="a-body">
              Filmmakers were the first AI video promised to serve and the first it let down: beautiful fragments, no
              story. Induce is built for the part that matters - whether the story works. It evaluates narrative before
              generation and orchestrates the best models underneath, so you direct a film instead of assembling
              fragments.
            </p>
            <div className="a-stats reveal">
              <div className="a-stat">
                <b>80%</b>
                <span>Less prompting vs. clip tools</span>
              </div>
              <div className="a-stat">
                <b>1 upload</b>
                <span>Whole script, one cut</span>
              </div>
              <div className="a-stat">
                <b>∞</b>
                <span>Timeline-wide continuity</span>
              </div>
              <div className="a-stat">
                <b>Every tier</b>
                <span>Full commercial rights</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DirectorTimeline />

      <FilmmakerHighlights />

      <section className="crew band-light" id="product">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- Who&apos;s on your crew</span>
            <h2>
              A Full Team Operating <em>Behind the Scenes.</em>
            </h2>
            <p className="sub">
              Every job that turns a script into a watchable scene - supervision, continuity, camera, post - runs
              inside Induce. The main job stays yours: directing.
            </p>
          </div>
          <div className="crew-list reveal">
            {[
              { dept: 'Dept 01', role: 'Script Supervisor', sub: 'Ingest & breakdown', what: <>Reads your screenplay end to end - FDX, PDF, Fountain, or paste - and turns <b>every line and cue into its own shot,</b> flagging anything that contradicts the story.</> },
              { dept: 'Dept 02', role: 'Continuity', sub: 'The continuity graph', what: <>Holds characters, wardrobe, locations, and props across the timeline. <b>Your lead looks like your lead in scene 1 and scene 40</b> - change page one and the cut updates.</> },
              { dept: 'Dept 03', role: 'Director of Photography', sub: 'Autonomous cinematography', what: <>A virtual DOP composes <b>framing, lens, and blocking from each beat</b> - wide for the silence, close for the confession.</> },
              { dept: 'Dept 04', role: 'First AD', sub: 'Your notes, executed', what: <>Give a note on any shot - angle, lens, light, pacing, performance - <b>and it lands without disturbing the scenes around it.</b> Local notes, not global re-rolls.</> },
              { dept: 'Dept 05', role: 'Post & Conform', sub: 'Multi-model orchestration', what: <>Routes each shot to the right engine - <b>Kling, Seedance, Veo, Nano Banana</b> - in one timeline, then conforms the cut for export or your editor.</> },
            ].map((row) => (
              <div key={row.dept} className="crew-row">
                <div className="dept">{row.dept}</div>
                <div className="role">
                  <p className="r">{row.role}</p>
                  <span className="sub">{row.sub}</span>
                </div>
                <p className="what">{row.what}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="usecases band-dark">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- What filmmakers ship with Induce</span>
            <h2>
              One pipeline for the <em>whole shot list.</em>
            </h2>
            <p className="sub">
              From a one-person short to a proof-of-concept, Induce fits the work you&apos;re already trying to make.
            </p>
          </div>
          <div className="uc-grid">
            {[
              { kind: 'camera_abstract' as const, slug: 'PREVIS', scene: 'USE 01', genre: 'PREVIS', kicker: 'Pre-production', title: 'Previsualization & animatics', body: 'Block a sequence and test coverage before you spend a day on set. See it as a watchable cut, then walk onto location knowing the shots.', tail: 'Camera · blocking · timing', d: '', video: CDN.videos.previs },
              { kind: 'dawn' as const, slug: 'SHORT FILM', scene: 'USE 02', genre: 'NARRATIVE', kicker: 'Narrative', title: 'Short films & series', body: 'Take a finished script to a complete cut with consistent characters throughout. Built for festival shorts, episodic concepts, and personal projects.', tail: 'Continuity · dialogue · scenes', d: ' d1', video: CDN.videos.shortFilm },
              { kind: 'warehouse' as const, slug: 'PITCH', scene: 'USE 03', genre: 'PITCH', kicker: 'Development', title: 'Pitch reels & proof-of-concept', body: 'Walk in with a sizzle that shows the world, not a deck that describes it. Turn a treatment into footage financiers can actually watch.', tail: 'Sizzle · lookbook · tone', d: '', video: CDN.videos.pitch },
              { kind: 'harbor' as const, slug: 'SPEC SPOT', scene: 'USE 04', genre: 'MUSIC', kicker: 'Spec & music', title: 'Music videos & spec spots', body: 'Cut visuals to a track or a brief and explore directions fast. Generate variations of a concept without rebuilding your world each time.', tail: 'Rhythm · variations · style', d: ' d1', video: CDN.videos.filmSpecSpot },
            ].map((uc) => (
              <article key={uc.slug} className={`uc-card reveal${uc.d}`}>
                <div className="art">
                  <VideoStill src={uc.video} aria-label={uc.title} />
                </div>
                <div className="body">
                  <span className="kicker">{uc.kicker}</span>
                  <h3>{uc.title}</h3>
                  <p>{uc.body}</p>
                  <span className="tail">{uc.tail}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="band-light">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- How Induce compares</span>
            <h2>
              Clip tools generate shots. Induce generates <em>films.</em>
            </h2>
            <p className="sub">
              Most AI video is a prompt box in a UI. Here&apos;s where a script-first pipeline pulls ahead for narrative work.
            </p>
          </div>
          <div className="compare-table-wrap reveal">
            <table className="vs-table">
              <caption className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                Comparison of Induce versus LTX Studio, Runway Gen-4, and single video models for filmmakers
              </caption>
              <colgroup>
                <col />
                <col className="c-induce" />
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">Capability</th>
                  <th scope="col" className="col-induce">
                    Induce<span className="role">Script-first pipeline</span>
                  </th>
                  <th scope="col">
                    LTX Studio<span className="role">All-in-one studio</span>
                  </th>
                  <th scope="col">
                    Runway Gen-4<span className="role">Clip / VFX suite</span>
                  </th>
                  <th scope="col">
                    Single models<span className="role">Veo · Kling</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row) => (
                  <tr key={row.capability}>
                    <th scope="row">{row.capability}</th>
                    <td className="cell-induce">
                      <VsCell cell={row.induce} />
                    </td>
                    <td>
                      <VsCell cell={row.ltx} />
                    </td>
                    <td>
                      <VsCell cell={row.runway} />
                    </td>
                    <td>
                      <VsCell cell={row.single} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="vs-note">
            Comparison reflects publicly available product information as of May 2026. Competitor names are trademarks of
            their respective owners.
          </p>
        </div>
      </section>

      <section className="proof-strip band-dark" aria-label="Key capabilities">
        <div className="container">
          <div className="ps-item">
            <span className="ps-val">
              80<sup>%</sup>
            </span>
            <span className="ps-label">Less prompting than clip-by-clip tools</span>
          </div>
          <div className="ps-div" aria-hidden="true" />
          <div className="ps-item">
            <span className="ps-val">∞</span>
            <span className="ps-label">Continuity across the entire timeline</span>
          </div>
          <div className="ps-div" aria-hidden="true" />
          <div className="ps-item">
            <span className="ps-val">4+</span>
            <span className="ps-label">Models orchestrated - Kling, Veo, Seedance, NB</span>
          </div>
          <div className="ps-div" aria-hidden="true" />
          <div className="ps-item">
            <span className="ps-val">1</span>
            <span className="ps-label">Upload. Whole script. First cut out.</span>
          </div>
        </div>
      </section>

      <section className="migrate band-light">
        <div className="container">
          <div className="migrate-card reveal">
            <div className="migrate-copy">
              <div className="kicker">- Migrating off Sora?</div>
              <h2>Sora is winding down. Your pipeline shouldn&apos;t.</h2>
              <p>
                With Sora&apos;s web, app, and API discontinued through 2026, filmmakers built on a single model are
                stranded. Induce orchestrates today&apos;s best models behind a script-first pipeline - so your continuity,
                overrides, and cut survive any one model&apos;s exit.
              </p>
            </div>
            <div className="act">
              <SignUpButton variant="primary" />
            </div>
          </div>
        </div>
      </section>

      <section className="band-dark">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- The problems Induce solves for filmmakers</span>
            <h2>
              Story, coverage, and <em>ownership.</em>
            </h2>
          </div>
          <div className="diffs">
            {[
              { n: '01', title: 'From screenplay to cut', body: 'Bring a finished script or shape one in the editor - FDX, PDF, or Fountain. Induce breaks it down, holds continuity, composes each shot with a virtual DOP, and assembles a sequence you can refine.', d: '' },
              { n: '02', title: 'The story stays whole', body: 'Story-state memory carries what each character knows and how they change across the film - so narrative logic holds from first frame to last.', d: ' d1' },
              { n: '03', title: 'You own what you make', body: 'Full commercial licensing on every tier. The frames, the cut, and your continuity model are yours - no training rights, no hidden carve-outs.', d: ' d2' },
            ].map((item) => (
              <div key={item.n} className={`diff reveal${item.d}`}>
                <div className="n">
                  <span className="line" /> {item.n}
                </div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFaqSection
        eyebrow="- Asked &amp; answered"
        title={
          <>
            Straight answers for
            <br />
            <em>filmmakers.</em>
          </>
        }
        items={FILMMAKERS_FAQ}
      />

      <section className="slate-cta band-dark" id="cta">
        <div className="container">
          <div className="slate reveal">
          <div className="clap" aria-hidden="true" />
          <div className="body">
            <div className="fields">
              <div className="field">
                <span className="l">PRODUCTION</span>
                <span className="v">Your Next Film</span>
              </div>
              <div className="field">
                <span className="l">DIRECTOR</span>
                <span className="v">You</span>
              </div>
              <div className="field">
                <span className="l">SCENE</span>
                <span className="v hot">01</span>
              </div>
              <div className="field">
                <span className="l">TAKE</span>
                <span className="v hot">01</span>
              </div>
              <div className="field">
                <span className="l">PLATFORM</span>
                <span className="v">Induce</span>
              </div>
            </div>
            <h2>
              Your next film is already written.
              <br />
              <em>Let&apos;s shoot it.</em>
            </h2>
            <div className="acts">
              <TalkToFoundersButton variant="primary" />
              <SignUpButton variant="ghost" />
            </div>
            <p className="note">Commercial rights on every tier · Priority access for production teams</p>
          </div>
          </div>
        </div>
      </section>
    </div>
  );
}
